import { useState, useEffect, useRef, useCallback } from "react";

// Main API - returns both pipeline status AND Amplify app details from MongoDB
const PTM_STATUS_API = process.env.REACT_APP_PTM_STATUS_API;

// Real-time Amplify status API - fetches current deployment status from AWS
const AMPLIFY_STATUS_API = process.env.REACT_APP_AMPLIFY_STATUS_API;

const POLL_INTERVAL = 4000; // Poll every 4 seconds
const INITIAL_DELAY = 3000; // Wait 3 seconds before first poll
const AMPLIFY_POLL_INTERVAL = 6000; // Poll Amplify every 6 seconds (slower since it hits AWS API)

const useProvisioningStatus = () => {
  // Backend (Terraform + Microservices) state
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(0);
  const [isPollingActive, setIsPollingActive] = useState(false);
  const [elapsed, setElapsed] = useState(0);

  // Frontend (Amplify) state
  const [amplifyData, setAmplifyData] = useState(null);
  const [amplifyProgress, setAmplifyProgress] = useState(0);
  const [amplifyError, setAmplifyError] = useState(null);

  const pollingIntervalRef = useRef(null);
  const amplifyPollingIntervalRef = useRef(null);
  const amplifyCompletedRef = useRef(false);
  const timerRef = useRef(null);

  // Store BOTH ExecutionIds
  const backendExecutionIdRef = useRef(null);
  const frontendExecutionIdRef = useRef(null);

  // Calculate backend progress (Terraform + Microservices)
  const calculateBackendProgress = useCallback((pipelineData) => {
    if (!pipelineData) return 0;

    let calculatedProgress = 0;
    const TERRAFORM_WEIGHT = 10;
    const TOTAL_CHILD_PIPELINES = 15;
    const CHILD_WEIGHT = 90 / TOTAL_CHILD_PIPELINES;

    // Terraform contribution (10%)
    if (pipelineData.State === "SUCCEEDED") {
      calculatedProgress += TERRAFORM_WEIGHT;
    } else if (
      pipelineData.State === "RUNNING" ||
      pipelineData.State === "IN_PROGRESS"
    ) {
      calculatedProgress += TERRAFORM_WEIGHT / 2;
    }

    // Child pipelines contribution (90%)
    if (pipelineData.ChildPipelines && pipelineData.ChildPipelines.length > 0) {
      const succeededCount = pipelineData.ChildPipelines.filter(
        (p) => p.State === "SUCCEEDED"
      ).length;
      calculatedProgress += succeededCount * CHILD_WEIGHT;
    }

    return Math.min(Math.round(calculatedProgress), 100);
  }, []);

  // Calculate Amplify progress
  const calculateAmplifyProgress = useCallback((amplifyApps) => {
    if (!amplifyApps || amplifyApps.length === 0) return 0;

    let totalBranches = 0;
    let successfulBranches = 0;

    amplifyApps.forEach((app) => {
      if (app.Branches && app.Branches.length > 0) {
        totalBranches += app.Branches.length;
        successfulBranches += app.Branches.filter(
          (branch) =>
            branch.Status === "SUCCEED" || branch.Status === "SUCCEEDED"
        ).length;
      }
    });

    if (totalBranches === 0) return 0;
    return Math.round((successfulBranches / totalBranches) * 100);
  }, []);

  // Check if backend deployment is complete
  const checkBackendCompletion = useCallback((pipelineData) => {
    if (!pipelineData) return false;

    const terraformComplete = pipelineData.State === "SUCCEEDED";
    const allChildrenComplete =
      pipelineData.ChildPipelines &&
      pipelineData.ChildPipelines.length > 0 &&
      pipelineData.ChildPipelines.every((p) => p.State === "SUCCEEDED");

    return terraformComplete && allChildrenComplete;
  }, []);

  // Check if Amplify deployment is complete
  const checkAmplifyCompletion = useCallback((amplifyApps) => {
    if (!amplifyApps || amplifyApps.length === 0) return false;

    return amplifyApps.every(
      (app) =>
        app.Branches &&
        app.Branches.length > 0 &&
        app.Branches.every(
          (branch) =>
            branch.Status === "SUCCEED" || branch.Status === "SUCCEEDED"
        )
    );
  }, []);

  // Stop polling functions
  const stopPolling = useCallback(() => {
    console.log("🛑 Stopping backend polling");

    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }

    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    setIsPollingActive(false);
    backendExecutionIdRef.current = null;
    frontendExecutionIdRef.current = null;
  }, []);

  const stopAmplifyPolling = useCallback(() => {
    console.log("🛑 Stopping Amplify polling");

    if (amplifyPollingIntervalRef.current) {
      clearInterval(amplifyPollingIntervalRef.current);
      amplifyPollingIntervalRef.current = null;
    }
    amplifyCompletedRef.current = true;
  }, []);

  // Fetch real-time Amplify status from AWS
  const fetchAmplifyStatus = useCallback(
    async (amplifyApps) => {
      if (!amplifyApps || amplifyApps.length === 0) return;

      try {
        console.log("🔍 Fetching real-time Amplify status from AWS");
        console.log(
          "📊 Amplify apps received:",
          JSON.stringify(amplifyApps, null, 2)
        );

        // Transform data for AmplifyStatusFetcher
        // MongoDB uses "ConnectedBranches", AmplifyStatusFetcher expects "Branches"
        const requestBody = {
          Apps: amplifyApps
            .filter((app) => {
              if (!app) {
                console.warn("⚠️ Null app found, skipping");
                return false;
              }
              if (!app.AppId) {
                console.warn("⚠️ App without AppId found:", app);
                return false;
              }
              return true;
            })
            .map((app) => {
              // Handle both "ConnectedBranches" (from MongoDB) and "Branches" (from API)
              // Ensure we always get an array, even if the value is undefined
              let branches = app.ConnectedBranches || app.Branches;

              // If branches is not an array (could be undefined, null, or other), use empty array
              if (!Array.isArray(branches)) {
                console.warn(
                  `⚠️ App ${app.AppName} has non-array branches:`,
                  branches
                );
                branches = [];
              }

              console.log(`📱 App ${app.AppName}:`, {
                hasConnectedBranches: !!app.ConnectedBranches,
                hasBranches: !!app.Branches,
                branchesValue: branches,
                branchesLength: branches.length,
              });

              const branchNames = branches
                .filter((b) => {
                  if (!b) {
                    console.warn(`⚠️ Null branch in app ${app.AppName}`);
                    return false;
                  }
                  if (!b.BranchName) {
                    console.warn(
                      `⚠️ Branch without BranchName in app ${app.AppName}:`,
                      b
                    );
                    return false;
                  }
                  return true;
                })
                .map((b) => b.BranchName);

              return {
                AppId: app.AppId,
                AppName: app.AppName || "",
                Branches: branchNames,
              };
            })
            .filter((app) => {
              if (app.Branches.length === 0) {
                console.warn(
                  `⚠️ App ${app.AppName} has no valid branches, skipping`
                );
                return false;
              }
              return true;
            }),
        };

        console.log(
          "📤 Amplify request:",
          JSON.stringify(requestBody, null, 2)
        );

        if (requestBody.Apps.length === 0) {
          console.warn("⚠️ No valid apps with branches to fetch status for");
          return;
        }

        const response = await fetch(AMPLIFY_STATUS_API, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(requestBody),
        });

        if (!response.ok) {
          throw new Error(`Amplify API Error ${response.status}`);
        }

        const amplifyStatusData = await response.json();
        console.log("✅ Amplify status received:", amplifyStatusData);

        // Update with real-time Amplify data
        setAmplifyData(amplifyStatusData.Apps);
        setAmplifyError(null);

        // Calculate Amplify progress
        const ampProgress = calculateAmplifyProgress(amplifyStatusData.Apps);
        setAmplifyProgress(ampProgress);

        // Check if Amplify deployment is complete
        if (checkAmplifyCompletion(amplifyStatusData.Apps)) {
          console.log("🎉 Amplify deployment completed!");
          if (amplifyPollingIntervalRef.current) {
            clearInterval(amplifyPollingIntervalRef.current);
            amplifyPollingIntervalRef.current = null;
          }
          amplifyCompletedRef.current = true;
        }
      } catch (error) {
        console.error("❌ Error fetching Amplify status:", error);
        setAmplifyError(error.message);
      }
    },
    [calculateAmplifyProgress, checkAmplifyCompletion]
  );

  // Fetch pipeline status from MongoDB
  const fetchPipelineStatus = useCallback(
    async (backendExecId) => {
      try {
        console.log(`[Poll] Polling status for ExecutionId: ${backendExecId}`);

        const response = await fetch(PTM_STATUS_API, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ExecutionId: backendExecId }),
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.warn(`⚠️ API Error ${response.status}: ${errorText}`);
          return;
        }

        const combinedData = await response.json();
        console.log("✅ Combined status received:", combinedData);

        // Set backend data
        setData(combinedData);
        setError(null);

        // Calculate backend progress
        const backendProg = calculateBackendProgress(combinedData);
        setProgress(backendProg);

        // Check if frontend data (Amplify apps) exists in the SAME document
        if (
          combinedData.HasFrontendData &&
          combinedData.AmplifyApps &&
          combinedData.AmplifyApps.length > 0
        ) {
          console.log(
            `✅ Frontend data detected with ${combinedData.AmplifyApps.length} Amplify apps`
          );

          // Set initial Amplify data from MongoDB
          
          // Calculate initial Amplify progress from MongoDB data
          // const initialAmpProgress = calculateAmplifyProgress(combinedData.AmplifyApps);
          // setAmplifyProgress(initialAmpProgress);
          
          // Start real-time Amplify polling if not already started
          if (
            !amplifyPollingIntervalRef.current &&
            !amplifyCompletedRef.current
          ) {
            console.log("🚀 Starting Amplify real-time polling");
            setAmplifyData(combinedData.AmplifyApps);

            // Fetch immediately
            fetchAmplifyStatus(combinedData.AmplifyApps);

            // Then set up interval
            amplifyPollingIntervalRef.current = setInterval(() => {
              fetchAmplifyStatus(combinedData.AmplifyApps);
            }, AMPLIFY_POLL_INTERVAL);
          }
        } else {
          console.log(
            "ℹ️ No frontend data yet (normal if frontend pipeline hasn't created Amplify apps)"
          );
        }

        // Check if backend deployment is complete
        if (checkBackendCompletion(combinedData)) {
          console.log("🎉 Backend deployment completed!");
          // Stop polling using refs directly to avoid circular dependency
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
          if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
          }
          setIsPollingActive(false);
        }
      } catch (error) {
        console.error("❌ Error fetching status:", error);
        setError(error.message);
      }
    },
    [
      calculateBackendProgress,
      calculateAmplifyProgress,
      checkBackendCompletion,
      fetchAmplifyStatus,
    ]
  );

  const handleFetchData = useCallback(
    ({ pipelineExecutionId, frontendExecutionId = null }) => {
      if (!pipelineExecutionId) {
        console.error("❌ No backend pipelineExecutionId provided");
        return;
      }

      console.log(
        `🚀 Starting polling for backend ExecutionId: ${pipelineExecutionId}`
      );
      if (frontendExecutionId) {
        console.log(
          `   (Frontend ExecutionId ${frontendExecutionId} stored for reference)`
        );
      }

      backendExecutionIdRef.current = pipelineExecutionId;
      frontendExecutionIdRef.current = frontendExecutionId; // Store but don't poll with it

      setIsPollingActive(true);
      setElapsed(0);
      setError(null);
      setAmplifyError(null);

      // Start elapsed time counter
      timerRef.current = setInterval(() => {
        setElapsed((prev) => prev + 1);
      }, 1000);

      // Initial delay before first poll
      setTimeout(() => {
        // Only poll with backend ExecutionId
        fetchPipelineStatus(backendExecutionIdRef.current);

        // Set up polling interval
        pollingIntervalRef.current = setInterval(() => {
          // Only poll with backend ExecutionId
          fetchPipelineStatus(backendExecutionIdRef.current);
        }, POLL_INTERVAL);
      }, INITIAL_DELAY);
    },
    [fetchPipelineStatus]
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopPolling();
      stopAmplifyPolling();
    };
  }, [stopPolling, stopAmplifyPolling]);

  return {
    // Backend data
    data,
    error,
    progress,
    isPollingActive,
    elapsed,
    handleFetchData,
    stopPolling,

    // Amplify data
    amplifyData,
    amplifyError,
    amplifyProgress,
    stopAmplifyPolling,
  };
};

export default useProvisioningStatus;
